import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { BiCheckCircle, BiErrorCircle, BiBlock } from 'react-icons/bi'
import { useUserLoggedInCheck } from '../../hooks/useUserLoggedInCheck'
import { RouteType } from '../../types/routes'
import { Box } from '../layout/Box'
import { ApplicationState } from '../../store'
import { getUserBookings } from '../../api/userBookings'
import { UserBooking, UserBookingStatusId } from '../../types/userBookings'
import { Typography } from '../layout/Typography'
import { Card } from '../layout/Card'
import { EmployeeAvatar } from '../../styles/components/employee'
import { Separator } from '../../styles/components/separator'
import styled from '../../utils/styled'
import { ActionButton } from '../../styles/components/buttons'
import { cancelAppointment } from '../cancelAppointment/api'
import { Loading } from '../data/Loading'
import { useLoadSalonData } from '../../hooks/useLoadSalonData'
import { getSalonGuidFromUrlParams } from '../../utils/urlParams'
import { getLocalStorageUserToken } from '../../utils/localStorage'

const checkIsBookingInThePast = (userBooking: UserBooking): boolean => {
  const [bookingIsoStringDate] = userBooking.datum.split('T')
  const bookingDateAndTimeIsoString = new Date([bookingIsoStringDate, userBooking.arbeitsbeginn].join('T'))

  const bookingDate = new Date(bookingDateAndTimeIsoString)

  return bookingDate < new Date()
}

export const UserBookings = () => {
  useUserLoggedInCheck(RouteType.Login)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [userBookings, setUserBookings] = useState<UserBooking[]>([])

  const salonGuid = useSelector((state: ApplicationState) => state.salon.data?.guid)

  const fetchUserBookings = async () => {
    const salonGuidFromUrlParams = getSalonGuidFromUrlParams()

    const userToken = getLocalStorageUserToken()

    if (!salonGuidFromUrlParams || !userToken) {
      return
    }
    setIsLoading(true)

    const fetchedUserBookings = await getUserBookings({ salonGuid: salonGuidFromUrlParams })

    if (fetchedUserBookings !== null) {
      setUserBookings(fetchedUserBookings)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    if (salonGuid) fetchUserBookings()
  }, [])
  useLoadSalonData({ onSalonDataFetch: fetchUserBookings })

  const getFutureAppointments = (): UserBooking[] => userBookings.filter(userBooking => !checkIsBookingInThePast(userBooking))

  const getPastAppointments = (): UserBooking[] => userBookings.filter(userBooking => checkIsBookingInThePast(userBooking))

  return (
    <Box>
      <Typography variant="h4">Termine</Typography>
      {isLoading && <Loading />}

      {userBookings.length === 0 ? (
        <Typography variant="body1">Keine Termine vorhanden</Typography>
      ) : (
        <>
          {getFutureAppointments().length >= 1 && (
            <Box margin="0 0 1rem">
              <Typography variant="bold">Kommende Termine</Typography>
            </Box>
          )}
          {getFutureAppointments()
            .reverse()
            .map(userBooking => (
              <UserBookingItem
                userBooking={userBooking}
                isUserBookingInThePast={false}
                setIsLoading={setIsLoading}
                fetchUserBookings={fetchUserBookings}
                key={userBooking.guid}
              />
            ))}
          {getPastAppointments().length >= 1 && (
            <Box margin="2rem 0 1rem">
              <Typography variant="bold">Vergangene Termine</Typography>
            </Box>
          )}
          {getPastAppointments().map(userBooking => (
            <UserBookingItem
              userBooking={userBooking}
              isUserBookingInThePast
              setIsLoading={setIsLoading}
              fetchUserBookings={fetchUserBookings}
              key={userBooking.guid}
            />
          ))}
        </>
      )}
    </Box>
  )
}

type UserBookingItemProps = {
  userBooking: UserBooking
  isUserBookingInThePast: boolean
  setIsLoading: (value: boolean) => void
  fetchUserBookings: () => void
}

const UserBookingItem = ({ userBooking, isUserBookingInThePast, setIsLoading, fetchUserBookings }: UserBookingItemProps) => {
  const salon = useSelector((state: ApplicationState) => state.salon.data)

  if (!salon) {
    return null
  }

  const getFullAppointmentDateAndTime = (bookingDate: string, bookingTime: string) => {
    const dateString = new Date(bookingDate).toLocaleDateString('de-DE', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    })

    return `${dateString}, ${bookingTime}`
  }

  const handleCancelAppointmentSubmit = async (userBookingGuid: string) => {
    setIsLoading(true)
    const response = await cancelAppointment(userBookingGuid)

    if (response !== null) {
      await fetchUserBookings()
    }
    setIsLoading(false)
  }

  const handleCancelAppointmentPress = async (userBookingGuid: string) => {
    const isConfirmed = window.confirm('Termin absagen - Bist du sicher?')

    if (isConfirmed) {
      handleCancelAppointmentSubmit(userBookingGuid)
    }
  }

  const checkIsBookingBeforeCancellationTime = (): boolean => {
    const [bookingIsoStringDate] = userBooking.datum.split('T')

    const bookingDate = new Date([bookingIsoStringDate, userBooking.arbeitsbeginn].join('T'))

    const cancellationTime = new Date(bookingDate.getTime() - salon.absageZeit * 60 * 60 * 1000)

    return new Date() < cancellationTime
  }

  const showCancelButton = () => {
    return (
      // only refStatusId 1 and 2 are cancellable
      (userBooking.refStatusId === 1 || userBooking.refStatusId === 2) &&
      // only show cancel button if the booking is in the future
      !isUserBookingInThePast
    )
  }

  return (
    <Card inactive={isUserBookingInThePast}>
      <Box flex alignItems="start" justifyContent="space-between" margin="0 0 1rem">
        <Box flex>
          <EmployeeAvatar src={userBooking.fotoUrl} alt={userBooking.nachname} />
          <Box>
            <Typography variant="body1" margin="0">
              {userBooking.vorname} {userBooking.nachname}
            </Typography>
            <Typography variant="body2" margin="0.25rem">
              {userBooking.title}
            </Typography>

            <Typography variant="body2" margin="0.25rem">
              {getFullAppointmentDateAndTime(userBooking.datum, userBooking.arbeitsbeginn)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box flex flexDirection="row" justifyContent="space-between" margin="0 0 1rem">
        <Box flex alignItems="center">
          <BookingStatusIcon statusId={userBooking.statusId} />
          <Typography variant="body1" margin="0">
            {userBooking.status}
          </Typography>
        </Box>

        {showCancelButton() && (
          <>
            {checkIsBookingBeforeCancellationTime() ? (
              <ActionButton onClick={() => handleCancelAppointmentPress(userBooking.guid)}>Stornieren</ActionButton>
            ) : (
              <ActionButton>
                <a href={`tel:${salon.kontakt.tel}`}>Anrufen</a>
              </ActionButton>
            )}
          </>
        )}
        {!checkIsBookingBeforeCancellationTime && <Typography variant="body2">Stornierung nicht mehr möglich</Typography>}
      </Box>

      {userBooking.services.length >= 1 && (
        <>
          <Separator />
          <Box margin="1rem 0 0">
            {userBooking.services.map(service => {
              return (
                <Box flex alignItems="end" key={service.guid}>
                  <Box margin="0 0.5rem 0 0">
                    <Typography variant="body1" margin="0">
                      {service.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" margin="0">
                    {service.minuten} min.
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </>
      )}
    </Card>
  )
}

const BookingStatusIcon = ({ statusId }: { statusId: UserBookingStatusId }) => {
  const getBookingStatusIcon = () => {
    switch (statusId) {
      case 'Success':
        return BiCheckCircle
      case 'Warning':
        return BiErrorCircle
      case 'Danger':
        return BiBlock
      default:
        return BiCheckCircle
    }
  }

  const BookingStatusIconElement = getBookingStatusIcon()

  const BookingStatusIconElementWithColor = styled(BookingStatusIconElement)`
    color: ${({ theme }) => {
      switch (statusId) {
        case 'Success':
          return theme.colors.accepted
        case 'Warning':
          return theme.colors.warning
        case 'Danger':
          return theme.colors.canceled
        default:
          return ''
      }
    }};
    font-size: ${props => props.theme.iconSizes.md};
  `

  return <BookingStatusIconElementWithColor />
}
